import Axios, { isAxiosError, isCancel } from 'axios';
import { buyFormToPassenger } from '@composable/_local/form&auto-reserve_id/buyFormToPassenger';
import { type GetReserveByPrePayTokenResponseDto } from '@clients/FlightApi';
import {
    BillApiApiBillGetCorportationInfoGet,
    type BillApiApiBillGetCorportationInfoGetRequest,
} from '@clients/BusApi';
import {
    EntityApiApiEntityAddUserEntityPost,
    EntityApiApiEntityConnectEntityToBillPost,
    EntityApiApiEntityGetUserEntitiesGet,
    EntityApiApiEntityUpdateUserEntityPut,
    type EntityApiApiEntityUpdateUserEntityPutRequest,
    type UserEntityDto,
    type UserEntityRequest,
} from '@clients/AccountingApi';
import {
    BillPaymentApiApiBillPaymentGatewaysPost,
    type PaymentServiceTypes,
} from '@clients/PaymentApi';
import {
    GiftCardApiApiGiftCardUserGiftCardsGet,
    GiftCardPaymentApiApiGiftCardPaymentGetBalanceGet,
    type UserGiftCardsResponse,
} from '@clients/GiftcardApi';
import {
    BillsApiApiBillsBillCodeAssignUserPut,
    CouponsApiApiBillsBillCodeCouponsDelete,
    UserScoresApiApiBillsBillCodeApplicableUserScoreGet,
    UserScoresApiApiBillsBillCodeApplyServiceDiscountPost,
    UserScoresApiApiBillsBillCodeApplyUserScorePost,
    UserScoresApiApiUserScoresRecievedGet,
} from '@clients/BillApi';
import {
    type ApplyCouponCodeRequestDto,
    CouponApiApiCouponApplyPost,
    Providers,
} from '@clients/CouponApi';
import type BillHandler from '@itinerary/BillHandler';
import HotelBillHandler from '@itinerary/hotel/HotelBillHandler';
import type ItineraryPackage from '@itinerary/ItineraryPackage';
import BusBillHandler from '@itinerary/trip/bus/BusBillHandler';
import FlightBillHandler from '@itinerary/trip/flight/FlightBillHandler';
import TrainBillHandler from '@itinerary/trip/train/TrainBillHandler';
import {
    CouponStatus,
    type DiscountEntry,
    discountTitles,
    DiscountType,
    type FinalizationStatus,
    type Gateway,
    GiftCardStatus,
    InformationRegisterStatus,
    type InvoiceEntry,
    type InvoiceSessionData,
    type ItineraryPackageInvoiceEntries,
    PaymentLoadingStatus,
    type RegisterPassengersParams,
} from '@store/invoice';
import ItineraryModule from '@itinerary/ItineraryModule';
import { PaxType } from '@utils/types';
import { ReserveType } from '@utils/itinerary';
import { Gender, Nationality, type SubmitBuyerPassenger } from '@store/form';
import { PaxTypeTitles } from '@itinerary/trip/TripModuleTypes';
import FlightAutoBillHandler from '@itinerary/trip/flight/FlightAutoBillHandler';
import { PrivacyItems } from '@itinerary/types';
import { RegisterPaxError } from '@itinerary/Exceptions';
import FlightPackage from '@itinerary/trip/flight/FlightPackage';

export class InvoiceStore {
    //#region state
    /**
     * Handler for the bill, that is used to prepare bill for finalization
     */
    billHandlers: BillHandler[] = [];

    haveCustomPrice = false;
    /**
     * Price entries of the bill
     */
    allEntries: ItineraryPackageInvoiceEntries[] = [];

    /**
     * Price entries of the pre bill
     */
    preEntries: ItineraryPackageInvoiceEntries[] = [];

    isLoadingPreEntries = false;

    isLoadingEntries = false;

    /**
     * List of discount entries
     */
    discounts: DiscountEntry[] = [];

    billCode: string | null = null;

    mac: string | null = null;

    /**
     * Code of discount coupon
     */
    couponCode: string | null = null;

    discountAbortController: AbortController | null = null;
    /**
     * Text of the message of the discount coupon
     */
    couponMessage = '';

    /**
     * The status of the discount coupon
     */
    couponStatus: CouponStatus = CouponStatus.None;
    couponAmount: number | undefined;

    billId: number | null = null;

    reserveId: number | null = null;

    informationRegisterStatus: InformationRegisterStatus =
        InformationRegisterStatus.None;

    registerInfoResult: any = null;

    /**
     * Information of passengers of the bill
     */
    pax: SubmitBuyerPassenger[] = [];

    /**
     * Email of the user which bill belongs to
     */
    email: string | null = null;

    /**
     * Mobile number of the user which bill belongs to
     */
    mobile: string | null = null;

    /**
     * Phone number of the user
     */
    landline: string | null = null;

    /**
     * Second mobile number of the user which bill belongs to
     */
    destinationMobile: string | null = null;

    /**
     * Customized data of the bill
     */
    customData: Record<any, any> | null = null;

    isLoadFromSession = false;

    /**
     * Indicates whether to use credit for payment or not
     */
    useCredit = false;
    creditPrivacy: PrivacyItems = PrivacyItems.All;
    giftCardPrivacy: PrivacyItems = PrivacyItems.All;

    gateway = '';
    gateWays: Gateway[] = [];
    mainGateways: Gateway[] = [];
    paymentLoading = PaymentLoadingStatus.None;
    paymentLinkInProgress = false;
    paymentLinkSentForBillCode: string | null = null;

    /**
     * Indicates whether to show the price change message or not
     */
    priceChangeMessageShown = false;

    /**
     * Applicable score on the bill
     */
    applicableScore: number | null = null;

    /**
     * The amount of money that will be discounted in exchange for user scores
     */
    applicableScoreAmount: number | null = null;

    /**
     * Current total score of the user
     */
    totalScore: number | null = null;

    editingItem = '';

    userEntities: UserEntityDto[] = [];

    /**
     * received club score from the current trip
     */
    receivedScore = 0;
    lastReceivedKey = '';

    giftCardStatus: GiftCardStatus = GiftCardStatus.None;
    // gift card codes will store and send to api in upperCase letters
    giftCardCode: string | null = null;
    giftCardMessage = '';
    giftCardAmount: number | null = null;
    userGiftCards: UserGiftCardsResponse[] = [];
    giftCardLoading = false;

    //#endregion

    /**
     * Sets the price entries of the bill
     */
    setEntries(entries: ItineraryPackageInvoiceEntries[]) {
        this.allEntries = entries.map(e => ({
            itineraryPackage: e.itineraryPackage,
            prices: e.prices.filter(item => item.count > 0),
        }));
    }

    get detailsMaxTimeForCheck() {
        return Math.max(
            ...this.billHandlers.map(handler => handler.detailsMaxTimeForCheck),
            120000
        );
    }

    get detailsMinTimeForCheck() {
        return this.billHandlers?.[0]?.detailsMinTimeForCheck ?? 70000;
    }

    get checkTimeBaseOnPax() {
        return this.billHandlers.every(handler => handler.checkTimeBaseOnPax);
    }

    get detailsTimeForCheck() {
        return this.pax.length > 1 && this.checkTimeBaseOnPax
            ? this.pax.length * this.detailsMinTimeForCheck
            : this.detailsMaxTimeForCheck;
    }

    /**
     * Returns all the price entries of the bill
     */
    get entries(): ItineraryPackageInvoiceEntries[] {
        return this.allEntries.map(entry => {
            const ent: ItineraryPackageInvoiceEntries = {
                itineraryPackage: entry.itineraryPackage,
                prices: entry.prices.map(entry => ({
                    paxType: entry.paxType,
                    title: entry.title || PaxTypeTitles[entry.paxType],
                    price: entry.price,
                    count: entry.count,
                })),
            };
            const optionalEntry = this.optionalServiceEntries.find(
                e => e.itineraryPackage === entry.itineraryPackage
            );
            if (optionalEntry) {
                ent.prices.push(...optionalEntry.prices);
            }
            return ent;
        });
    }

    get payFullyWithCredit() {
        const authStore = useAuthStore();
        const userCredit = authStore.credit || 0;
        return this.useCredit && userCredit >= this.totalPriceUnformatted;
    }

    get payFullyWithGiftCard() {
        return (
            this.giftCardStatus === GiftCardStatus.Accepted &&
            this.totalPriceUnformatted === 0 &&
            !this.payFullyWithCredit
        );
    }

    /**
     * clear pre entries and final entries
     */
    clearAllEntries() {
        this.setEntries([]);
        this.preEntries = [];
    }

    /**
     * change count of entry price of entry index by paxTypes
     */
    changeEntryPriceCountByPaxType(data: {
        entryIndex: number;
        paxType: PaxType;
        count: number;
    }) {
        const priceIndex = this.allEntries[data.entryIndex].prices.findIndex(
            p => p.paxType === data.paxType
        );
        this.allEntries[data.entryIndex].prices[priceIndex].count = data.count;
    }

    /**
     * Apply a discount to bill's price
     */
    addDiscount(params: { type: DiscountType; price: number }) {
        if (params.price <= 0) {
            return;
        }
        this.discounts.push({
            type: params.type,
            price: params.price,
            title: discountTitles[params.type],
        });
    }

    /**
     * Returns discount entries of the bill
     */
    get titledDiscounts() {
        const discounts = this.discounts.map(entry => ({
            type: entry.type,
            title: discountTitles[entry.type],
            price: entry.price,
        }));
        return this.giftCardAmount
            ? [
                  ...discounts,
                  {
                      title: 'اعتبار سفر کارت',
                      price: this.giftCardAmount,
                      type: DiscountType.Special,
                  },
              ]
            : discounts;
    }

    /**
     * Removes a discount from bill's price
     */
    removeDiscount(type: DiscountType) {
        const start = this.discounts.findIndex(d => d.type === type);
        if (start !== -1) {
            this.discounts.splice(start, 1);
        }
    }

    setBillInfo(params: {
        code: string | null;
        id: number | null;
        reserveId: number | null;
        mac?: string | null;
    }) {
        this.billCode = params.code;
        this.billId = params.id;
        this.reserveId = params.reserveId;
        this.mac = params?.mac || null;
    }

    setCoupon(params: {
        message: string;
        code: string | null;
        status: CouponStatus;
        amount?: number;
    }) {
        this.couponCode = params.code;
        this.couponMessage = params.message;
        this.couponStatus = params.status;
        this.couponAmount =
            typeof params.amount === 'number' ? params.amount : undefined;
    }

    clearCoupon() {
        this.setCoupon({
            code: null,
            message: '',
            status: CouponStatus.None,
        });
    }

    /**
     * Sets the contact information of the user which bill belongs to
     */
    setContact(params: {
        email: string;
        mobile: string;
        landline: string;
        destinationMobile: string;
    }) {
        this.email = params.email;
        this.mobile = params.mobile;
        this.landline = params.landline;
        this.destinationMobile = params.destinationMobile;
    }

    /**
     * Sets the information of passengers of the bill
     */
    setTitledPax(pax: SubmitBuyerPassenger[]) {
        this.pax = pax.map(p => ({
            ...p,
            title: this.billHandlers[0]?.getTitle(p.type!, p.gender!) || '',
        }));
    }

    /**
     * Clears all information of state of bill module
     */
    clearState() {
        this.setEntries([]);
        this.preEntries = [];
        this.discounts = [];
        this.giftCardAmount = null;
        this.giftCardStatus = GiftCardStatus.None;
        this.giftCardCode = null;
        this.giftCardMessage = '';
        this.billCode = null;
        this.paymentLinkSentForBillCode = null;
        this.useCredit = false;
        this.mac = null;
        this.clearCoupon();
        this.gateWays = [];
        this.applicableScore = null;
        this.applicableScoreAmount = null;
        this.totalScore = null;
        this.billId = null;
        this.informationRegisterStatus = InformationRegisterStatus.None;
        this.pax = [];
        this.isLoadFromSession = false;
        this.creditPrivacy = PrivacyItems.All;
        this.giftCardPrivacy = PrivacyItems.All;
        sessionStorage.removeItem('invoiceData');
    }

    /**
     * Sets applicable score by the user for discount
     */
    setApplicableScores(payload: {
        amount: number;
        score: number;
        totalScore: number;
    }) {
        this.applicableScore = payload.score;
        this.applicableScoreAmount = payload.amount;
        this.totalScore = payload.totalScore;
    }

    /**
     * Returns the total price of the bill(considering discounts)
     */
    get totalPrice() {
        return this.totalPriceUnformatted;
    }

    get showNonCashPayment() {
        return useFormStore().itineraryPackages.some(pkg => pkg.canUseDiscount);
    }

    /**
     * Returns the total price of the bill, as a number
     */
    get totalPriceUnformatted() {
        return Math.max(
            this.entries.reduce(
                (acc, curr) =>
                    acc +
                    curr.prices.reduce(
                        (pacc, pcurr) => pacc + pcurr.price * pcurr.count,
                        0
                    ),
                0
            ) -
                this.discounts.reduce((acc, curr) => acc + curr.price, 0) -
                (this.giftCardAmount || 0),
            0
        );
    }

    get preInvoiceTotalPrice(): number {
        return this.preEntries.reduce(
            (acc, curr) =>
                acc +
                curr.prices.reduce(
                    (pacc, pcurr) => pacc + pcurr.price * pcurr.count,
                    0
                ),
            0
        );
    }

    get preInvoiceTotalDiscount(): number {
        return this.preEntries.reduce(
            (acc, curr) =>
                acc +
                curr.prices
                    .filter(p => !!p.discount)
                    .reduce(
                        (pacc, pcurr) => pacc + pcurr.discount! * pcurr.count,
                        0
                    ),
            0
        );
    }

    /**
     * Returns the total amount of discount of pre-bill
     */
    get preInvoiceDiscounts() {
        const discountType = this.preEntries.every(f =>
            f.prices.every(p => p.discountType === DiscountType.FirstPurchase)
        )
            ? DiscountType.FirstPurchase
            : DiscountType.Special;
        return <DiscountEntry[]>[
            {
                type: discountType,
                title: discountTitles[discountType],
                price: this.preInvoiceTotalDiscount,
            },
        ].filter(d => d.price > 0);
    }

    get preInvoiceEntries(): ItineraryPackageInvoiceEntries[] {
        return this.preEntries.map(p => ({
            ...p,
            prices: p.prices.map(pr => ({
                ...pr,
                price: pr.price + pr.discount!,
                title: pr.title || PaxTypeTitles[pr.paxType],
            })),
        }));
    }

    /**
     * Returns the total price of the bill without considering discounts
     */
    get totalPriceWithoutDiscount() {
        return this.totalPriceWithoutDiscountUnformatted;
    }

    /**
     * Returns the total price of the bill, before the discounts being applied
     */
    get totalPriceWithoutDiscountUnformatted() {
        return this.entries.reduce(
            (acc, curr) =>
                acc +
                curr.prices.reduce(
                    (pacc, pcurr) => pacc + pcurr.price * pcurr.count,
                    0
                ),
            0
        );
    }

    get optionalServiceEntries(): ItineraryPackageInvoiceEntries[] {
        const formStore = useFormStore();
        const groupedServices: ItineraryPackageInvoiceEntries[] = [];
        formStore.paxForms.forEach(pax => {
            pax.optionalServiceList.forEach(s => {
                if (s.selectedService && s.selectedService.id !== 'none') {
                    const service = groupedServices.find(
                        e => e.itineraryPackage === s.itineraryPackage
                    );
                    if (service) {
                        const serviceItem = service.prices.find(
                            p => p.title === s.selectedService!.label
                        );
                        if (serviceItem) {
                            serviceItem.count++;
                        } else {
                            service.prices.push({
                                title: s.selectedService.label,
                                count: 1,
                                paxType: pax.type,
                                price: s.selectedService.price,
                            });
                        }
                    } else {
                        const entry: ItineraryPackageInvoiceEntries = {
                            itineraryPackage: s.itineraryPackage!,
                            prices: [],
                        };
                        entry.prices.push({
                            title: s.selectedService.label,
                            count: 1,
                            paxType: pax.type,
                            price: s.selectedService.price,
                        });
                        groupedServices.push(entry);
                    }
                }
            });
            pax.freeServiceList.forEach(s => {
                s.selectedServices?.forEach(selectedService => {
                    if (selectedService) {
                        const service = groupedServices.find(
                            e => e.itineraryPackage === s.itineraryPackage
                        );
                        if (service) {
                            const serviceItem = service.prices.find(
                                p => p.title === selectedService!.label
                            );
                            if (serviceItem) {
                                serviceItem.count++;
                            } else {
                                service.prices.push({
                                    title: selectedService.label,
                                    count: 1,
                                    paxType: pax.type,
                                    price: selectedService.price,
                                });
                            }
                        } else {
                            const entry: ItineraryPackageInvoiceEntries = {
                                itineraryPackage: s.itineraryPackage!,
                                prices: [],
                            };
                            entry.prices.push({
                                title: selectedService.label,
                                count: 1,
                                paxType: pax.type,
                                price: selectedService.price,
                            });
                            groupedServices.push(entry);
                        }
                    }
                });
            });
        });
        return groupedServices.map(gs => ({
            itineraryPackage: gs.itineraryPackage,
            prices: gs.prices.filter(Boolean).map(p => ({
                ...p,
                totalPrice: p.price * p.count,
            })),
        }));
    }

    /**
     * Applies discount coupon to bill
     */
    async applyCoupon(params: { code: string }) {
        if (params.code === 'newmrbilit-DF07') {
            // bad code
            params.code = 'unique';
        }
        // make sure that the current coupon handled
        this.couponStatus = CouponStatus.Checking;
        await this.cancelCoupon(CouponStatus.Checking);
        try {
            const applyCouponCodeRequestDto: ApplyCouponCodeRequestDto = {
                couponCode: params.code,
                billCode: this.billCode,
            };
            const smId = useSafarMarket().getSmId();
            if (smId) {
                applyCouponCodeRequestDto.source = Providers.NUMBER_3;
                applyCouponCodeRequestDto.sourceToken = smId;
            }
            const { data } = await CouponApiApiCouponApplyPost({
                applyCouponCodeRequestDto,
            });
            if (data.isApplied) {
                this.setCoupon({
                    message: data.message || '',
                    code: params.code,
                    status: CouponStatus.Accepted,
                    amount: data.discountValue,
                });
                this.removeDiscount(DiscountType.FirstPurchase);
                this.addDiscount({
                    type: DiscountType.Coupon,
                    price: data.discountValue || 0,
                });
            } else {
                this.removeDiscount(DiscountType.FirstPurchase);
                this.setCoupon({
                    message: data.message || '',
                    code: '',
                    status: CouponStatus.Rejected,
                });
            }
        } catch (ex) {
            reportException(ex);
            if (isAxiosError(ex)) {
                this.setCoupon({
                    message: ex.response?.data.message,
                    code: '',
                    status: CouponStatus.Rejected,
                });
            }
        }
    }

    setGiftCard(params: {
        amount?: number;
        code: string | null;
        status: GiftCardStatus;
        message?: string | null;
        remainedBalance?: number | null;
    }) {
        this.giftCardStatus = params.status;
        this.giftCardAmount = params.amount || null;
        this.giftCardCode = params.code?.toUpperCase() || null;
        this.giftCardMessage = params.message || '';
    }

    removeGiftCard() {
        this.setGiftCard({ code: null, status: GiftCardStatus.None });
    }

    async terminateReserveActions() {
        if (this.reserveId) {
            await useCurrentItineraryModule().value.terminatePackageReserve(
                this.reserveId
            );
        }
    }

    async applyGiftCard(giftCardCode: string) {
        this.setGiftCard({
            code: null,
            status: GiftCardStatus.Checking,
        });

        try {
            const { data } =
                await GiftCardPaymentApiApiGiftCardPaymentGetBalanceGet({
                    giftCardCode: giftCardCode.toUpperCase(),
                    amount: this.totalPriceUnformatted,
                    billCode: this.billCode || undefined,
                });
            this.setGiftCard({
                amount: data.appliedAmount,
                code: giftCardCode,
                message: data.massage,
                status: GiftCardStatus.Accepted,
            });
        } catch (e: any) {
            reportException(e);
            this.setGiftCard({
                code: null,
                message: e.response?.data?.message,
                status: GiftCardStatus.Rejected,
            });
        }
    }

    async loadGiftCards() {
        this.giftCardLoading = true;
        try {
            const { data } = await GiftCardApiApiGiftCardUserGiftCardsGet();
            this.userGiftCards = data.map(giftCard => ({
                ...giftCard,
                giftCardCode: giftCard.giftCardCode?.toUpperCase(),
            }));
        } catch (e) {
            reportException(e);
        } finally {
            this.giftCardLoading = false;
        }
    }

    /**
     * Cancels using discount coupon
     */
    async cancelCoupon(status: CouponStatus = CouponStatus.None) {
        await this.billDiscountRemover();
        this.removeDiscount(DiscountType.Coupon);
        this.setCoupon({
            message: '',
            code: '',
            status,
        });
    }

    initializeGateway() {
        const { utmSource } = storeToRefs(usePostHog());
        if (sessionStorage.getItem('rayanCard') === 'true') {
            this.gateway = 'RayanCard';
        } else if (
            sessionStorage.getItem('tara') === 'true' ||
            utmSource.value === 'tara'
        ) {
            this.gateway = 'tara';
        } else {
            this.gateway = '';
        }
    }

    get eventMetric() {
        const authStore = useAuthStore();
        const giftCardAccepted =
            this.giftCardStatus === GiftCardStatus.Accepted;
        const selectedClubCoin = this.discounts.some(
            d => d.type === DiscountType.Score
        );
        const totalGiftCardPayment = giftCardAccepted
            ? Math.min(this.totalPrice, this.giftCardAmount ?? 0)
            : 0;

        const totalWalletPayment = this.useCredit
            ? Math.min(this.totalPrice, authStore.credit ?? 0)
            : 0;
        const totalNonCashPayment = totalWalletPayment + totalGiftCardPayment;
        return {
            billCode: this.billCode,
            reserveId: this.reserveId,
            discount: this.discounts.reduce(
                (prev, next) => prev + next.price,
                0
            ),
            totalPrice: this.totalPrice,
            totalWalletPayment,
            totalGatewayPayment:
                this.totalPrice < totalNonCashPayment
                    ? 0
                    : this.totalPrice - totalNonCashPayment,
            selectedGateway: this.gateway,
            totalGiftCardPayment,
            giftCardCode: this.giftCardCode,
            couponCode: this.couponCode,
            couponCodeDiscount: this.couponAmount,
            clubCoinDiscount: selectedClubCoin
                ? this.applicableScoreAmount
                : undefined,
            vpn: authStore.vpn,
        };
    }

    saveSession(persistent = false) {
        const storage = persistent ? localStorage : sessionStorage;
        storage.setItem(
            'invoiceData',
            JSON.stringify({
                pax: this.pax, // pure
                contact: {
                    email: this.email || '',
                    mobile: this.mobile || '',
                    landline: this.landline || '',
                    destinationMobile: this.destinationMobile || '',
                }, // pure
                customData: this.customData, // pure
                billInfo: {
                    code: this.billCode,
                    id: this.billId,
                    reserveId: this.reserveId,
                    mac: this.mac,
                },
                haveCustomPrice: this.haveCustomPrice,
                paymentLinkSentForBillCode: this.paymentLinkSentForBillCode,
                gateway: this.gateway, // pure
                useCredit: this.useCredit, // pure
                discounts: this.discounts, // pure
                creditPrivacy: this.creditPrivacy, // pure
                giftCardPrivacy: this.giftCardPrivacy, // pure
                entries: this.entries, // impure
                billHandlers: this.billHandlers, // impure
            })
        );
    }

    private normalizePaxType(paxType?: string | null): PaxType {
        switch (paxType) {
            case 'ADL':
                return PaxType.Adult;
            case 'CHD':
                return PaxType.Child;
            case 'INF':
                return PaxType.Infant;
            default:
                return PaxType.Adult;
        }
    }

    async loadPayment(reserveData: GetReserveByPrePayTokenResponseDto) {
        if (
            !reserveData ||
            [
                InformationRegisterStatus.Registered,
                InformationRegisterStatus.RegisteredFromSession,
            ].includes(this.informationRegisterStatus)
        ) {
            return;
        }
        const { FlightPackage: pkgData, BillDetail, Passengers } = reserveData;
        const paxInfo = {
            adultCount: BillDetail?.AdultCount ?? 0,
            childCount: BillDetail?.ChildCount ?? 0,
            infantCount: BillDetail?.InfantCount ?? 0,
        };

        const flightPkg = new FlightPackage(pkgData, paxInfo, {});

        const entries = await flightPkg.loadPreInvoice(paxInfo);
        const data: InvoiceSessionData = {
            entries: [
                {
                    itineraryPackage: flightPkg,
                    prices: entries,
                },
            ],
            billHandlers: [new FlightBillHandler('', [])],
            discounts: [],
            pax:
                Passengers?.map(passenger =>
                    buyFormToPassenger(
                        {
                            birthDate: passenger.BirthDay
                                ? new Date(passenger.BirthDay)
                                : null,
                            firstName: passenger.FirstName,
                            gender: passenger.Male
                                ? Gender.Male
                                : Gender.Female,
                            lastName: passenger.LastName,
                            nationalCode: passenger.NationalCode ?? '',
                            nationality: passenger.NationalCode
                                ? Nationality.Iranian
                                : Nationality.Foreigner,
                            passportExpiry: passenger.PassportExpiration
                                ? new Date(passenger.PassportExpiration ?? '')
                                : null,
                            passportNumber: passenger.PassportNumber ?? '',
                            persianFirstName: passenger.PersianFirstName ?? '',
                            persianLastName: passenger.PersianLastName ?? '',
                            nationalityCode: passenger.Nationality ?? '',
                            isDuplicated: false,
                            carModel: '',
                            'carPlate.0': '',
                            'carPlate.1': '',
                            'carPlate.2': '',
                            'carPlate.3': '',
                            'carPlate.4': '',
                        },
                        this.normalizePaxType(passenger.PaxType)
                    )
                ) ?? [],
            billInfo: {
                code: BillDetail?.BillCode ?? '',
                id: Number(BillDetail?.Id) ?? null,
                reserveId: null,
            },
            customData: undefined,
            haveCustomPrice: false,
            gateway: this.gateway,
            contact: {
                mobile: reserveData.BillDetail?.Mobile ?? '',
                destinationMobile: '',
                landline: '',
                email: reserveData.BillDetail?.Email ?? '',
            },
            useCredit: this.useCredit,
            creditPrivacy: PrivacyItems.All,
            giftCardPrivacy: PrivacyItems.All,
        };
        this.setTitledPax(data.pax);
        this.setContact(data.contact);
        this.customData = data.customData;
        this.useCredit = data.useCredit;
        this.discounts = data.discounts;
        this.haveCustomPrice = !!data?.haveCustomPrice;
        this.creditPrivacy = data?.creditPrivacy;
        this.setEntries(
            data.entries.map(entry => ({
                ...entry,
                itineraryPackage: entry.itineraryPackage
                    ? ItineraryModule.revivePackage(entry.itineraryPackage)!
                    : undefined,
            }))
        );
        this.billHandlers = data.billHandlers.map(handler => {
            const concrete = new FlightBillHandler('', []);
            Object.assign(concrete!, handler);
            return concrete!;
        });
        this.isLoadFromSession = true;
        this.informationRegisterStatus =
            InformationRegisterStatus.RegisteredFromSession;
        this.setBillInfo(data.billInfo);
    }

    loadSession() {
        const dataStr =
            sessionStorage.getItem('invoiceData') ||
            localStorage.getItem('invoiceData');
        if (
            !dataStr ||
            [
                InformationRegisterStatus.Registered,
                InformationRegisterStatus.RegisteredFromSession,
            ].includes(this.informationRegisterStatus)
        ) {
            return;
        }
        const data = JSON.parse(dataStr) as InvoiceSessionData;
        this.paymentLinkSentForBillCode =
            data.paymentLinkSentForBillCode ?? null;
        this.setTitledPax(data.pax);
        this.setContact(data.contact);
        this.customData = data.customData;
        this.useCredit = data.useCredit;
        this.discounts = data.discounts;
        this.haveCustomPrice = !!data?.haveCustomPrice;
        this.creditPrivacy = data?.creditPrivacy;
        this.giftCardPrivacy = data?.giftCardPrivacy;
        this.setEntries(
            data.entries.map(entry => ({
                ...entry,
                itineraryPackage: entry.itineraryPackage
                    ? ItineraryModule.revivePackage(entry.itineraryPackage)!
                    : undefined,
            }))
        );
        this.billHandlers = data.billHandlers.map(handler => {
            let concrete: BillHandler | undefined;
            switch (handler.concreteType) {
                case ReserveType.Train:
                    concrete = new TrainBillHandler();
                    break;
                case ReserveType.Hotel:
                    concrete = new HotelBillHandler();
                    break;
                case ReserveType.Bus:
                    concrete = new BusBillHandler(0, 0, false);
                    break;
                case ReserveType.Flight:
                    if (handler.isAutoReserve) {
                        concrete = new FlightAutoBillHandler();
                    } else {
                        concrete = new FlightBillHandler('', []);
                    }
                    break;
            }
            Object.assign(concrete!, handler);
            return concrete!;
        });
        this.isLoadFromSession = true;
        this.informationRegisterStatus =
            InformationRegisterStatus.RegisteredFromSession;
        this.setBillInfo(data.billInfo);
    }

    /**
     * Registers the information of passengers of bill
     */
    async registerPaxInfo(params: RegisterPassengersParams) {
        const autoReserveStore = useAutoReserveStore();
        const { isAutoReserve } = autoReserveStore;
        if (isAutoReserve) {
            await autoReserveStore.createReserve();
        }
        if (this.billId === null || this.billCode === null) {
            return;
        }
        try {
            if (this.billHandlers === null) {
                return;
            }
            const results: boolean[] = [];
            for (const index in this.billHandlers) {
                const handler = this.billHandlers[index];
                try {
                    const result = await handler[
                        isAutoReserve
                            ? 'autoReserveRegisterPaxInfo'
                            : 'registerPaxInfo'
                    ]({
                        ...params,
                        billId: this.billId,
                        billCode: this.billCode,
                        entries: this.entries[0]?.prices || [],
                        bypassPaxCountValidation: params.formDirty,
                    });
                    // stores a single result !!!!!!!
                    this.registerInfoResult = result.data || null;
                    results.push(result.result);
                } catch (e) {
                    if (e instanceof RegisterPaxError) {
                        const { isConfirmed } = await swal({
                            title: 'هشدار',
                            text: e.message,
                            confirmButtonText: 'ادامه',
                            cancelButtonText: 'بازگشت',
                            icon: 'warning',
                            showCancelButton: true,
                        });
                        if (isConfirmed) {
                            this.registerInfoResult = e.data || null;
                            results.push(e.result);
                        } else {
                            throw e;
                        }
                    } else {
                        throw e;
                    }
                }
            }

            this.setTitledPax(params.pax);
            this.setContact({
                email: params.email,
                mobile: params.mobile,
                landline: params.landline,
                destinationMobile: params.destinationMobile,
            });
            this.customData = params.customData;
            this.saveSession(params.floating);
            return results.every(Boolean);
        } catch (ex) {
            this.informationRegisterStatus = InformationRegisterStatus.Error;
            throw ex;
        }
    }

    /**
     * Checks the bill before payment whether it had prepay and whether it is allowed for payment or not
     */
    async checkBillBeforePayment() {
        if (this.billCode === null) {
            return;
        }

        if (this.billHandlers === null) {
            return;
        }
        const unifiedHandlers = this.billHandlers.reduce((all, curr) => {
            if (all.some(b => b.concreteType === curr.concreteType)) {
                return all;
            } else {
                return [...all, curr];
            }
        }, [] as BillHandler[]);
        await Promise.all(
            unifiedHandlers.map(h =>
                h.prepayReservation(this.billCode!, {
                    price: this.totalPriceUnformatted,
                    useCredit: this.useCredit,
                })
            )
        );
    }

    /**
     * Processes the user to gateway for bill payment
     */
    async proceedToGateway(byLink?: Boolean) {
        this.discountAbortController?.abort();
        const { isWebApp } = useDevice();
        let link = await useAutoReserveStore().proceedPayment();
        if (!link) {
            const gatewayInfo = this.gateWays.find(
                gateway => gateway.gatewayRedirectParameter === this.gateway
            );
            const innerGatewayId =
                this.gateway && gatewayInfo?.mainGatewayId
                    ? `&LinkedGatewayId=${gatewayInfo.gatewayId}`
                    : '';
            const gateway =
                this.gateway && gatewayInfo?.mainGatewayId
                    ? `&gateway=${gatewayInfo?.mainGatewayId}`
                    : this.gateway
                      ? `&gateway=${this.gateway}`
                      : '';
            const giftCardCode = this.giftCardCode
                ? `&giftCardCode=${this.giftCardCode}`
                : '';
            const isByPaymentLink = `&IsByPaymentLink=${!!byLink}`;
            await this.applyCustomDiscount();
            link = `${useBaseUrls().payment}/api/billpayment/${
                this.billCode
            }?payFromCredit=${this.useCredit}&access_token=${
                useAuthStore().token
            }${gateway}${giftCardCode}${innerGatewayId}${isByPaymentLink}`;
        }
        this.paymentLoading = PaymentLoadingStatus.Redirecting;
        if (isWebApp.value) {
            window.setTimeout(() => {
                this.paymentLoading = PaymentLoadingStatus.None;
            }, 1000);
        }
        window.location.href = link;
    }

    async applyCustomDiscount() {
        if (this.haveCustomPrice) {
            try {
                this.reloadDiscountAbortController();
                await UserScoresApiApiBillsBillCodeApplyServiceDiscountPost(
                    {
                        billCode: this.billCode || '',
                    },
                    { signal: this.discountAbortController?.signal }
                );
            } catch (e) {
                if (!isCancel(e)) {
                    throw e;
                }
            }
        }
    }

    async assignBillToLoggedInUser(xPlayerID?: string | null) {
        if (!this.billCode || !xPlayerID) {
            return;
        }
        try {
            await BillsApiApiBillsBillCodeAssignUserPut({
                billCode: this.billCode,
                xPlayerID,
            });
        } catch (e) {
            reportException(e);
        }
    }

    async loadApplicableScore() {
        if (
            !this.billCode ||
            !useAuthStore().loggedIn ||
            this.applicableScore !== null
        ) {
            return;
        }
        const { data } =
            await UserScoresApiApiBillsBillCodeApplicableUserScoreGet({
                billCode: this.billCode,
            });
        this.setApplicableScores({
            amount: data.ApplicableAmount || 0,
            score: data.ApplicableScore || 0,
            totalScore: data.Score || 0,
        });
    }

    reloadDiscountAbortController() {
        this.discountAbortController?.abort();
        this.discountAbortController = new AbortController();
    }

    async applyUserScore() {
        if (!this.billCode || !useAuthStore().loggedIn) {
            return;
        }
        try {
            this.reloadDiscountAbortController();
            await UserScoresApiApiBillsBillCodeApplyUserScorePost(
                {
                    billCode: this.billCode,
                },
                { signal: this.discountAbortController?.signal }
            );
            this.addDiscount({
                type: DiscountType.Score,
                price: this.applicableScoreAmount || 0,
            });
        } catch (e) {
            if (!isCancel(e)) {
                throw e;
            }
        }
    }

    async billDiscountRemover() {
        if (this.billCode) {
            try {
                this.reloadDiscountAbortController();
                await CouponsApiApiBillsBillCodeCouponsDelete(
                    {
                        billCode: this.billCode || '',
                    },
                    {
                        signal: this.discountAbortController?.signal,
                    }
                );
            } catch (e) {
                if (!isCancel(e)) {
                    throw e;
                }
            }
        } else {
            throw new Error('set bill code');
        }
    }

    async cancelUserScore() {
        await this.billDiscountRemover();
        this.removeDiscount(DiscountType.Score);
    }

    async getTicketFile(data: {
        billCode: string;
        mac: string;
    }): Promise<FinalizationStatus> {
        Axios.defaults.headers.common.Authorization = '';
        return (
            await Axios.get(
                `${useBaseUrls().finalize}/workflow/bill/${
                    data.billCode
                }/status?mac=${data.mac}`
            )
        ).data;
    }

    setPreEntryOfPackage(data: {
        itineraryPackage: ItineraryPackage;
        entries: InvoiceEntry[];
    }) {
        const entry = this.preEntries.find(
            e => e.itineraryPackage === data.itineraryPackage
        );
        if (entry) {
            entry.prices = data.entries;
        } else {
            this.preEntries.push({
                itineraryPackage: data.itineraryPackage,
                prices: data.entries,
            });
        }
    }

    /**
     * Loads fares of the currently selected price class and provides information for the prebill to be rendered
     */
    async loadPreInvoice(itineraryPackage: ItineraryPackage) {
        const { getPassCount } = useSearchFormStore();
        if (!itineraryPackage?.selectedPrices?.length) {
            this.setPreEntryOfPackage({
                itineraryPackage,
                entries: [],
            });
            return;
        }
        this.isLoadingPreEntries = true;
        const entries = await itineraryPackage.loadPreInvoice({
            ...getPassCount(itineraryPackage),
            loggedIn: useAuthStore().loggedIn,
        });
        this.isLoadingPreEntries = false;

        this.setPreEntryOfPackage({
            itineraryPackage,
            entries,
        });
    }

    async loadUserEntities() {
        try {
            const { data } = await EntityApiApiEntityGetUserEntitiesGet();
            this.userEntities = data;
        } catch (e) {
            reportException(e);
        }
    }

    async addUserEntity(request: UserEntityRequest) {
        try {
            await EntityApiApiEntityAddUserEntityPost({
                userEntityRequest: request,
            });
            return true;
        } catch (e) {
            reportException(e);
            return false;
        }
    }

    async updateUserEntity(
        request: EntityApiApiEntityUpdateUserEntityPutRequest
    ) {
        try {
            await EntityApiApiEntityUpdateUserEntityPut(request);
            return true;
        } catch (e) {
            reportException(e);
            return false;
        }
    }

    async connectEntityToBill(entityId: number) {
        try {
            await EntityApiApiEntityConnectEntityToBillPost({
                billId: this.billId || undefined,
                entityId,
            });
        } catch (e) {
            reportException(e);
        }
    }

    async loadCorporationInfo(
        params: BillApiApiBillGetCorportationInfoGetRequest
    ) {
        try {
            // void type returned
            const { data }: { data: unknown } =
                await BillApiApiBillGetCorportationInfoGet(params);
            return data as { phone: string };
        } catch (e) {
            reportException(e);
        }
    }

    async loadReceivedTripScore() {
        const formStore = useFormStore();
        const authStore = useAuthStore();
        if (this.billCode && authStore.loggedIn) {
            const receivedKey = `${this.billCode}-${formStore.adultCount}-${formStore.childCount}-${formStore.infantCount}`;
            if (this.lastReceivedKey === receivedKey) {
                return;
            } else {
                this.lastReceivedKey = receivedKey;
            }
            try {
                const { data } = await UserScoresApiApiUserScoresRecievedGet({
                    billCode: this.billCode,
                    adultCount: formStore.adultCount,
                    childCount: formStore.childCount,
                    infantCount: formStore.infantCount,
                });
                this.receivedScore = data.Score || 0;
            } catch (e) {
                reportException(e);
                this.receivedScore = 0;
                this.lastReceivedKey = '';
            }
        } else {
            this.receivedScore = 0;
            this.lastReceivedKey = '';
        }
    }

    //#region gateways
    async getGateways() {
        const form = useCurrentSearchForm();
        if (this.gateWays.length) {
            return;
        }
        const { data } = await BillPaymentApiApiBillPaymentGatewaysPost({
            serviceType: form.value.reserveInfo
                .type as unknown as PaymentServiceTypes,
        });
        this.gateWays =
            data?.gateways?.flatMap(gateway =>
                gateway.linkedGateways?.length
                    ? gateway.linkedGateways?.map(linkedGateway => ({
                          ...linkedGateway,
                          mainGatewayId: gateway.gatewayId,
                      }))
                    : gateway
            ) || [];
        this.mainGateways = this.gateWays?.filter(({ isMain }) => isMain) || [];
        this.gateway = this.gateWays[0]?.gatewayRedirectParameter || '';
    }
    //#endregion
}

export const { use: useInvoiceStore } = defineClassStore(
    'invoice',
    () => new InvoiceStore()
);
